import { FC } from 'react'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { useGetMemberCentersQuery } from '../../redux/services/memberCenterService'
import {
  AgiosLogo,
  BeamTransparentLogo,
  BluebirdLogo,
  BristolMeyersLogo,
  ChiesiLogo,
  EditasLogo,
  FulcrumLogo,
  GamidaCellTransparentLogo,
  MedunikTransparentLogo,
  NovoNordiskLogo,
  PfizerLogo,
  QuiltHealthColorLogo,
  SanofiLogo,
  TerumoLogo,
  VertexLogo
} from 'assets'

interface Sponsor {
  name: string
  logo: string
  link: string
  description: string
}

export const Sponsors: FC = () => {
  const { data: memberCenters } = useGetMemberCentersQuery()

  // Round down to nearest 5
  const centerCount = memberCenters
    ? Math.floor(memberCenters.length / 5) * 5
    : '120+'

  // All sponsors in alphabetical order
  const sponsors: Sponsor[] = [
    {
      name: 'Agios',
      logo: AgiosLogo,
      link: 'https://www.agios.com/',
      description:
        'Agios is a biopharmaceutical company that discovers and develops novel treatments for patients living with rare diseases. Their focus includes developing innovative treatments for rare blood disorders.'
    },
    {
      name: 'BEAM Therapeutics',
      logo: BeamTransparentLogo,
      link: 'https://www.beamtx.com/',
      description:
        'BEAM Therapeutics is pioneering the use of base editing, a revolutionary gene editing technology, to develop precision genetic medicines for patients suffering from serious diseases.'
    },
    {
      name: 'Bluebird Bio',
      logo: BluebirdLogo,
      link: 'https://www.bluebirdbio.com/',
      description:
        'Bluebird Bio is pioneering gene therapy research and treatments for severe genetic diseases and cancer, with a focus on developing transformative treatments for conditions including sickle cell disease.'
    },
    {
      name: 'Bristol Myers Squibb',
      logo: BristolMeyersLogo,
      link: 'https://www.bms.com/',
      description:
        'Bristol Myers Squibb is a global biopharmaceutical company whose mission is to discover, develop and deliver innovative medicines that help patients prevail over serious diseases.'
    },
    {
      name: 'Chiesi Global Rare Diseases',
      logo: ChiesiLogo,
      link: 'https://www.chiesi.com/',
      description:
        'Chiesi Global Rare Diseases is dedicated to delivering innovative therapies and solutions for people affected by rare diseases, including those with sickle cell disease.'
    },
    {
      name: 'Editas Medicine',
      logo: EditasLogo,
      link: 'https://www.editasmedicine.com/',
      description:
        'Editas Medicine is a leading genome editing company focused on translating the power and potential of CRISPR genome editing into a broad class of transformative genomic medicines.'
    },
    {
      name: 'Fulcrum Therapeutics',
      logo: FulcrumLogo,
      link: 'https://www.fulcrumtx.com/',
      description:
        'Fulcrum Therapeutics is a clinical-stage biopharmaceutical company focused on improving the lives of patients with genetically defined rare diseases in areas of high unmet medical need.'
    },
    {
      name: 'Gamida Cell',
      logo: GamidaCellTransparentLogo,
      link: 'https://www.gamida-cell.com/',
      description:
        'Gamida Cell is a cell therapy pioneer working to turn cells into powerful therapeutics, with a proprietary NAM technology platform that expands cells while maintaining their functional characteristics.'
    },
    {
      name: 'Medunik',
      logo: MedunikTransparentLogo,
      link: 'https://www.medunik.com/',
      description:
        'Medunik USA is a specialty pharmaceutical company dedicated to providing access to orphan drugs for patients suffering from rare diseases in the United States.'
    },
    {
      name: 'Novo Nordisk',
      logo: NovoNordiskLogo,
      link: 'https://www.novonordisk.com/',
      description:
        'Novo Nordisk is a global healthcare company with more than 95 years of innovation and leadership in diabetes care, now expanding its focus to include treatments for rare blood disorders.'
    },
    {
      name: 'Pfizer',
      logo: PfizerLogo,
      link: 'https://www.pfizer.com/',
      description:
        "Pfizer is one of the world's premier biopharmaceutical companies, committed to developing breakthrough therapies that change patients' lives, including innovative treatments for rare diseases."
    },
    {
      name: 'Quilt Health',
      logo: QuiltHealthColorLogo,
      link: 'https://www.quilthealth.com/',
      description:
        'Quilt Health is a digital health company dedicated to accelerating the path of novel therapies for complex diseases through clinical trials and to market. By integrating research with care, leveraging clinician and patient networks, and deploying cutting-edge technology, Quilt Health streamlines the clinical trial process to improve enrollment, retention, and trial efficiency. Founded in 2024, Quilt Health is initially focused on sickle cell disease, the most common rare disease in the U.S., affecting African, Middle Eastern, and Mediterranean populations. Learn more by clicking the link above.'
    },
    {
      name: 'Sanofi',
      logo: SanofiLogo,
      link: 'https://www.sanofi.com/',
      description:
        'Sanofi is a global biopharmaceutical company focused on human health. They prevent illness with vaccines, provide innovative treatments to fight pain and ease suffering.'
    },
    {
      name: 'Terumo',
      logo: TerumoLogo,
      link: 'https://www.terumo.com/',
      description:
        'Terumo is a global leader in medical technology, committed to "Contributing to Society through Healthcare" with innovative medical devices and solutions that improve patient care.'
    },
    {
      name: 'Vertex',
      logo: VertexLogo,
      link: 'https://www.vrtx.com/',
      description:
        'Vertex is a global biotechnology company that invests in scientific innovation to create transformative medicines for people with serious diseases, including treatments for sickle cell disease.'
    }
  ]

  return (
    <main className="min-h-screen bg-[#EDE6DE] px-4 py-12 md:px-8 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-serif text-[#3C2416] mb-6">
          Sponsoring NASCC
        </h1>

        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-[#3C2416]">
            NASCC sponsorship is a great way to get your company name in front
            of the major decision makers and center directors at our more than{' '}
            {centerCount} member centers (and growing!). Not only that, but
            sponsoring NASCC means your company is helping to increase the
            standard of care for sickle cell patients around the nation.
          </p>
          <p className="text-[#3C2416]">
            If you are interested in becoming a sponsor, please reach out to{' '}
            <a
              href="mailto:sponsors@sicklecellcenters.org"
              className="text-[#A6410A] hover:underline"
            >
              sponsors@sicklecellcenters.org
            </a>
            .
          </p>
        </div>

        <h2 className="text-3xl font-serif text-[#3C2416] mb-8">
          Thanks to our current sponsors!
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {sponsors.map((sponsor) => (
            <div
              key={sponsor.name}
              className="bg-white rounded-lg shadow-lg overflow-hidden"
            >
              <div className="p-6">
                <h3 className="text-2xl font-serif mb-4">
                  <a
                    href={sponsor.link}
                    className="text-[#3C2416] hover:text-[#A6410A] transition-colors inline-flex items-center gap-2 group"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {sponsor.name}
                    <ChevronRightIcon className="h-5 w-5 transform group-hover:translate-x-1 transition-transform" />
                  </a>
                </h3>
                <div className="flex justify-center items-center h-24 pb-4">
                  <a
                    href={sponsor.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity"
                  >
                    <img
                      src={sponsor.logo}
                      alt={`${sponsor.name} logo`}
                      className={
                        sponsor.name === 'Quilt Health'
                          ? 'max-h-20 max-w-[280px] w-auto object-contain scale-[3.5]'
                          : sponsor.name === 'Medunik'
                            ? 'max-h-14 max-w-[200px] w-auto object-contain scale-[2.5]'
                            : sponsor.name === 'Bristol Myers Squibb'
                              ? 'max-h-14 max-w-[200px] h-14 w-auto object-contain'
                              : sponsor.name === 'Novo Nordisk'
                                ? 'max-h-20 max-w-[200px] w-auto object-contain'
                                : 'max-h-14 max-w-[200px] w-auto object-contain'
                      }
                    />
                  </a>
                </div>
                <p className="text-[#3C2416]">{sponsor.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  )
}

export default Sponsors
